import apiClient from './apiService';

export type DeficiencySource = 'testing' | 'audit' | 'monitoring' | 'self_assessment';
export type DeficiencySeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational';
export type DeficiencyStatus = 'Open' | 'In-Progress' | 'Resolved' | 'Closed';

export const DEFICIENCY_SOURCES: DeficiencySource[] = ['testing', 'audit', 'monitoring', 'self_assessment'];
export const DEFICIENCY_SEVERITIES: DeficiencySeverity[] = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
export const DEFICIENCY_STATUSES: DeficiencyStatus[] = ['Open', 'In-Progress', 'Resolved', 'Closed'];

export interface ControlDeficiency {
  id: number;
  entity_id: number;
  control_id: number;
  control_code?: string;
  control_name?: string;
  source: DeficiencySource;
  source_ref_id?: number | null;
  source_test_name?: string | null;
  geography_name?: string | null;
  title?: string | null;
  description: string;
  detected_severity: DeficiencySeverity;
  status: DeficiencyStatus;
  detected_on: string;
  owner_user_id?: number | null;
  owner_name?: string | null;
  remediation_notes?: string | null;
  is_active: number;
  created_by?: number | null;
  created_by_name?: string | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface CreateDeficiencyPayload {
  description: string;
  title?: string;
  source?: DeficiencySource;
  source_ref_id?: number | null;
  detected_severity?: DeficiencySeverity;
  owner_user_id?: number | null;
}

export interface UpdateDeficiencyPayload {
  title?: string;
  description?: string;
  detected_severity?: DeficiencySeverity;
  status?: DeficiencyStatus;
  owner_user_id?: number | null;
  remediation_notes?: string;
}

const BASE = '/api/v1/ctr';

export const controlDeficiencyService = {
  async listForControl(
    controlId: number,
    opts?: { source?: DeficiencySource; status?: DeficiencyStatus; source_ref_id?: number },
  ): Promise<ControlDeficiency[]> {
    const params: Record<string, string | number> = {};
    if (opts?.source) params.source = opts.source;
    if (opts?.status) params.status = opts.status;
    if (opts?.source_ref_id != null) params.source_ref_id = opts.source_ref_id;
    const resp = await apiClient.get(`${BASE}/controls/${controlId}/deficiencies`, { params });
    return resp.data?.data ?? [];
  },

  async listAll(opts?: {
    source?: DeficiencySource;
    status?: DeficiencyStatus;
    severity?: DeficiencySeverity;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: ControlDeficiency[]; total: number }> {
    const params: Record<string, string | number> = { limit: opts?.limit ?? 200 };
    if (opts?.source)   params.source   = opts.source;
    if (opts?.status)   params.status   = opts.status;
    if (opts?.severity) params.severity = opts.severity;
    if (opts?.search)   params.search   = opts.search;
    if (opts?.page)     params.page     = opts.page;
    const resp = await apiClient.get(`${BASE}/deficiencies`, { params });
    return { data: resp.data?.data ?? [], total: resp.data?.meta?.total ?? 0 };
  },

  async create(controlId: number, payload: CreateDeficiencyPayload): Promise<ControlDeficiency> {
    const resp = await apiClient.post(`${BASE}/controls/${controlId}/deficiencies`, payload);
    return resp.data?.data;
  },

  async update(id: number, payload: UpdateDeficiencyPayload): Promise<void> {
    await apiClient.put(`${BASE}/deficiencies/${id}`, payload);
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/deficiencies/${id}`);
  },
};

export default controlDeficiencyService;
